import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CircularProgress from '@mui/material/CircularProgress';
import {
  createFeatureRequestTicket,
  createRunSupportTicket,
  createSupportTicket,
  getJiraStatus,
  loadJiraProjects,
  addJiraTicketComment,
  getJiraTicketComments,
  updateJiraTicketComment,
} from '../services/jira.service';
import Richtexteditor from '../ui/Richtexteditor';
import './SupportChatBot.css';
import SupportChatBotLottieIcon from './SupportChatBotLottieIcon';

interface SupportChatBotProps {
  hidden?: boolean;
}

type SupportTab = 'feature' | 'support' | 'resources' | 'run' | 'tickets';

type TrackedTicketType = 'feature' | 'support';

interface TrackedTicket {
  key: string;
  type: TrackedTicketType;
  summary: string;
  createdAt: string;
}

interface JiraCommentItem {
  id: string;
  author: string;
  created?: string;
  updated?: string;
  body: string;
}

interface SupportChatPrefillEventDetail {
  summary?: string;
  description?: string;
  projectKey?: string;
  attachments?: File[];
}

const RICH_TEXT_EMPTY_VALUE = '<p><br></p>';

const getRichTextPlainText = (value: string) =>
  (value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const hasRichTextContent = (value: string) => getRichTextPlainText(value).length > 0;

const sanitizeRichTextValue = (content: string) => {
  const trimmed = (content || '').trim();

  if (trimmed === RICH_TEXT_EMPTY_VALUE) {
    return '';
  }

  return hasRichTextContent(trimmed) ? trimmed : '';
};

const parseLabelsInput = (value: string) =>
  (value || '')
    .split(',')
    .map((label) => label.trim())
    .filter((label) => label.length > 0);

const SupportChatBot: React.FC<SupportChatBotProps> = ({ hidden }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<SupportTab>('feature');
  const [projects, setProjects] = useState<Array<{ key: string; name: string }>>([]);
  const [projectsLoading, setProjectsLoading] = useState<boolean>(false);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [featureSummary, setFeatureSummary] = useState<string>('');
  const [featureDescription, setFeatureDescription] = useState<string>('');
  const [featureLabelsInput, setFeatureLabelsInput] = useState<string>('');
  const [featureError, setFeatureError] = useState<string | null>(null);
  const [submittingTicket, setSubmittingTicket] = useState<boolean>(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [featureSuccess, setFeatureSuccess] = useState<string | null>(null);
  const [supportSummary, setSupportSummary] = useState<string>('');
  const [supportDescription, setSupportDescription] = useState<string>('');
  const [supportError, setSupportError] = useState<string | null>(null);
  const [supportSuccess, setSupportSuccess] = useState<string | null>(null);
  const [supportSubmitting, setSupportSubmitting] = useState<boolean>(false);
  const [supportAttachments, setSupportAttachments] = useState<File[]>([]);
  const [runOverviewSummary, setRunOverviewSummary] = useState<string>('');
  const [runOverviewDescription, setRunOverviewDescription] = useState<string>('');
  const [runOverviewLabelsInput, setRunOverviewLabelsInput] = useState<string>('');
  const [runOverviewError, setRunOverviewError] = useState<string | null>(null);
  const [runOverviewSuccess, setRunOverviewSuccess] = useState<string | null>(null);
  const [runOverviewSubmitting, setRunOverviewSubmitting] = useState<boolean>(false);
  const [runOverviewAttachments, setRunOverviewAttachments] = useState<File[]>([]);
  const [expandedTextarea, setExpandedTextarea] = useState<string | null>(null);
  const [trackedTickets, setTrackedTickets] = useState<TrackedTicket[]>([]);
  const [ticketLookup, setTicketLookup] = useState<string>('');
  const [selectedTicketKey, setSelectedTicketKey] = useState<string>('');
  const [ticketDetails, setTicketDetails] = useState<any | null>(null);
  const [ticketDetailsLoading, setTicketDetailsLoading] = useState<boolean>(false);
  const [ticketDetailsError, setTicketDetailsError] = useState<string | null>(null);
  const [ticketComments, setTicketComments] = useState<JiraCommentItem[]>([]);
  const [ticketCommentsLoading, setTicketCommentsLoading] = useState<boolean>(false);
  const [commentsError, setCommentsError] = useState<string | null>(null);
  const [commentDraft, setCommentDraft] = useState<string>('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentDraft, setEditingCommentDraft] = useState<string>('');
  const [commentSubmitting, setCommentSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const stored = window.localStorage.getItem('lens-support-chatbot:tickets');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setTrackedTickets(
            parsed
              .filter((item) => typeof item?.key === 'string')
              .map((item) => ({
                key: item.key,
                type: (item.type === 'support' ? 'support' : 'feature') as TrackedTicketType,
                summary: item.summary || '',
                createdAt: item.createdAt || new Date().toISOString(),
              }))
          );
        }
      }
    } catch {
      // ignore corrupted storage values
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem('lens-support-chatbot:tickets', JSON.stringify(trackedTickets));
  }, [trackedTickets]);

  const normalizeCommentBody = (value: string) =>
    (value || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const formatDateTime = (value?: string) => {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const registerTicket = useCallback(
    (ticketKey: string, type: TrackedTicketType, summary: string) => {
      if (!ticketKey) {
        return;
      }

      const normalizedSummary = summary?.trim() ? summary.trim() : 'Untitled request';

      setTrackedTickets((previous) => {
        const existing = previous.filter((ticket) => ticket.key !== ticketKey);
        const nextTicket: TrackedTicket = {
          key: ticketKey,
          type,
          summary: normalizedSummary,
          createdAt: new Date().toISOString(),
        };

        return [nextTicket, ...existing].slice(0, 6);
      });
    },
    []
  );

  const normalizeComments = useCallback((response: any): JiraCommentItem[] => {
    if (!response) {
      return [];
    }

    const commentsSource = Array.isArray(response)
      ? response
      : Array.isArray(response?.comments)
      ? response.comments
      : [];

    return commentsSource
      .filter((item) => item)
      .map((item) => {
        const id = String(item.id || item.commentId || '');
        return {
          id,
          author:
            item.author?.displayName ||
            item.author?.name ||
            item.updateAuthor?.displayName ||
            'Unknown teammate',
          created: item.created,
          updated: item.updated,
          body: normalizeCommentBody(item.body || item.comment || ''),
        } as JiraCommentItem;
      })
      .filter((comment) => comment.id);
  }, []);

  const extractTicketLabels = (labels: any): string[] => {
    if (!labels) {
      return [];
    }

    if (Array.isArray(labels)) {
      return labels.map((label) => String(label)).filter(Boolean);
    }

    if (Array.isArray(labels?.items)) {
      return labels.items
        .map((label: any) => String(label?.name || label?.value || label))
        .filter(Boolean);
    }

    return [];
  };

  const refreshTicketComments = useCallback(
    async (issueKey: string) => {
      if (!issueKey) {
        return;
      }

      setCommentsError(null);
      setTicketCommentsLoading(true);

      try {
        const response = await getJiraTicketComments(issueKey);
        setTicketComments(normalizeComments(response));
      } catch (error: any) {
        const message =
          error?.response?.data?.data?.messages?.join(', ') ||
          error?.message ||
          'Unable to load ticket comments right now.';
        setCommentsError(message);
        setTicketComments([]);
      } finally {
        setTicketCommentsLoading(false);
      }
    },
    [normalizeComments]
  );

  const loadTicketInsights = useCallback(
    async (issueKey: string) => {
      if (!issueKey) {
        return;
      }

      const normalizedKey = issueKey.trim().toUpperCase();
      setTicketLookup(normalizedKey);
      setSelectedTicketKey(normalizedKey);
      setTicketDetailsError(null);
      setCommentsError(null);
      setTicketDetails(null);
      setTicketComments([]);
      setCommentDraft('');
      setEditingCommentId(null);
      setEditingCommentDraft('');
      setTicketDetailsLoading(true);

      try {
        const response = await getJiraStatus(normalizedKey);
        setTicketDetails(response || null);
        await refreshTicketComments(normalizedKey);
      } catch (error: any) {
        const message =
          error?.response?.data?.data?.messages?.join(', ') ||
          error?.message ||
          'Unable to load Jira ticket details. Please double-check the ticket key.';
        setTicketDetailsError(message);
        setTicketDetails(null);
      } finally {
        setTicketDetailsLoading(false);
      }
    },
    [refreshTicketComments]
  );

  const handleTicketLookupSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedKey = ticketLookup.trim().toUpperCase();

    if (!normalizedKey) {
      setTicketDetailsError('Enter a Jira ticket key to continue.');
      return;
    }

    setActiveTab('tickets');
    loadTicketInsights(normalizedKey);
  };

  const handleSelectTrackedTicket = (ticket: TrackedTicket) => {
    setActiveTab('tickets');
    loadTicketInsights(ticket.key);
  };

  const handleRemoveTrackedTicket = (ticketKey: string) => {
    setTrackedTickets((previous) => previous.filter((ticket) => ticket.key !== ticketKey));
  };

  const handleAddComment = async () => {
    if (!selectedTicketKey || !commentDraft.trim()) {
      return;
    }

    setCommentSubmitting(true);
    setCommentsError(null);

    try {
      await addJiraTicketComment(selectedTicketKey, commentDraft.trim());
      setCommentDraft('');
      await refreshTicketComments(selectedTicketKey);
    } catch (error: any) {
      const message =
        error?.response?.data?.data?.messages?.join(', ') ||
        error?.message ||
        'Unable to add a comment to this Jira ticket right now.';
      setCommentsError(message);
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleStartEditComment = (comment: JiraCommentItem) => {
    setEditingCommentId(comment.id);
    setEditingCommentDraft(comment.body);
    setCommentsError(null);
  };

  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setEditingCommentDraft('');
  };

  const handleUpdateComment = async () => {
    if (!selectedTicketKey || !editingCommentId) {
      return;
    }

    const trimmed = editingCommentDraft.trim();
    if (!trimmed) {
      setCommentsError('Comment text cannot be empty.');
      return;
    }

    setCommentSubmitting(true);
    setCommentsError(null);

    try {
      await updateJiraTicketComment(selectedTicketKey, editingCommentId, trimmed);
      setEditingCommentId(null);
      setEditingCommentDraft('');
      await refreshTicketComments(selectedTicketKey);
    } catch (error: any) {
      const message =
        error?.response?.data?.data?.messages?.join(', ') ||
        error?.message ||
        'Unable to update this comment right now. Please try again shortly.';
      setCommentsError(message);
    } finally {
      setCommentSubmitting(false);
    }
  };

  const createAttachmentSignature = (file: File) => `${file.name}-${file.size}-${file.lastModified}`;

  const isSupportedAttachment = (file: File) => {
    if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
      return true;
    }

    if (!file.type) {
      return /\.(png|jpe?g|gif|bmp|webp|svg|mp4|mov|avi|mpe?g|webm)$/i.test(file.name);
    }

    return false;
  };

  const mergeAttachments = (current: File[], incoming: File[]) => {
    const mediaFiles = incoming.filter(isSupportedAttachment);
    const existingSignatures = new Set(current.map(createAttachmentSignature));
    const newFiles = mediaFiles.filter((file) => {
      const signature = createAttachmentSignature(file);
      if (existingSignatures.has(signature)) {
        return false;
      }

      existingSignatures.add(signature);
      return true;
    });

    return {
      next: newFiles.length > 0 ? [...current, ...newFiles] : current,
      added: newFiles.length,
      processed: mediaFiles.length,
    };
  };

  const toggleTextareaExpansion = (field: string) => {
    setExpandedTextarea((previous) => (previous === field ? null : field));
  };

  useEffect(() => {
    const needsProjects = activeTab === 'feature' || activeTab === 'run' || activeTab === 'support';

    if (isOpen && needsProjects && !projectsLoading && projects.length === 0) {
      setProjectsLoading(true);
      loadJiraProjects()
        .then((response) => {
          if (Array.isArray(response)) {
            setProjects(response);
            if (response.length > 0) {
              setSelectedProject((previous) => previous || response[0].key || '');
            }
          }
        })
        .catch(() => {
          const message = 'Unable to load Jira projects at the moment.';
          if (activeTab === 'feature') {
            setFeatureError(message);
          }
          if (activeTab === 'support') {
            setSupportError(message);
          }
          if (activeTab === 'run') {
            setRunOverviewError(message);
          }
        })
        .finally(() => {
          setProjectsLoading(false);
        });
    }
  }, [activeTab, isOpen, projects.length, projectsLoading]);

  const resetSupportFormState = () => {
    setSupportSummary('');
    setSupportDescription('');
    setSupportError(null);
    setSupportAttachments([]);
    setSupportSubmitting(false);
    setExpandedTextarea((previous) => (previous === 'support-description' ? null : previous));
  };

  const resetRunOverviewState = () => {
    setRunOverviewSummary('');
    setRunOverviewDescription('');
    setRunOverviewLabelsInput('');
    setRunOverviewError(null);
    setRunOverviewSuccess(null);
    setRunOverviewSubmitting(false);
    setRunOverviewAttachments([]);
    setExpandedTextarea((previous) =>
      previous === 'run-summary' || previous === 'run-details' ? null : previous,
    );
  };

  const handleToggle = () => {
    if (isOpen) {
      resetFeatureFormState();
      setFeatureSuccess(null);
      resetSupportFormState();
      setSupportSuccess(null);
      resetRunOverviewState();
      setIsExpanded(false);
    } else {
      setFeatureSuccess(null);
      setSupportSuccess(null);
      setRunOverviewSuccess(null);
    }

    setActiveTab('feature');
    setIsOpen((previous) => !previous);
    setFeatureError(null);
    setSupportError(null);
    setRunOverviewError(null);
    setExpandedTextarea(null);
  };

  const togglePanelExpansion = () => {
    setIsExpanded((previous) => !previous);
  };

  const resetFeatureFormState = () => {
    setFeatureSummary('');
    setFeatureDescription('');
    setFeatureLabelsInput('');
    setFeatureError(null);
    setAttachments([]);
    setExpandedTextarea((previous) => (previous === 'feature-description' ? null : previous));
  };

  useEffect(() => {
    const handlePrefill = (event: Event) => {
      const { summary, description, projectKey, attachments: incomingAttachments } =
        (event as CustomEvent<SupportChatPrefillEventDetail>).detail || {};

      resetFeatureFormState();
      resetSupportFormState();
      setSupportSuccess(null);
      setSupportError(null);
      resetRunOverviewState();
      setActiveTab('run');
      setIsOpen(true);
      setExpandedTextarea(null);

      if (projectKey) {
        setSelectedProject(projectKey);
      }

      if (Array.isArray(incomingAttachments) && incomingAttachments.length > 0) {
        setRunOverviewAttachments([...incomingAttachments]);
      }

      if (typeof summary === 'string') {
        setRunOverviewSummary(sanitizeRichTextValue(summary));
      }

      if (typeof description === 'string') {
        setRunOverviewDescription(sanitizeRichTextValue(description));
      }
    };

    window.addEventListener('support-chatbot:prefill', handlePrefill as EventListener);

    return () => {
      window.removeEventListener('support-chatbot:prefill', handlePrefill as EventListener);
    };
  }, []);

  const handleCloseFeatureForm = () => {
    resetFeatureFormState();
    setFeatureSuccess(null);
    setIsOpen(false);
    resetSupportFormState();
    setSupportSuccess(null);
    resetRunOverviewState();
    setExpandedTextarea(null);
  };

  const handleRemoveRunOverviewAttachment = (index: number) => {
    setRunOverviewSuccess(null);
    setRunOverviewError(null);
    setRunOverviewAttachments((previous) => previous.filter((_, attachmentIndex) => attachmentIndex !== index));
  };

  const handleCreateRunOverviewTicket = async () => {
    setRunOverviewSuccess(null);

    if (!hasRichTextContent(runOverviewSummary) || !hasRichTextContent(runOverviewDescription)) {
      setRunOverviewError('Run overview details are required before contacting support.');
      return;
    }

    const projectKey = selectedProject || projects[0]?.key;
    const summaryText = getRichTextPlainText(runOverviewSummary);

    if (!projectKey) {
      setRunOverviewError('A Jira project is required to contact support.');
      return;
    }

    setRunOverviewSubmitting(true);
    setRunOverviewError(null);

    try {
      const ticket = await createRunSupportTicket({
        projectKey,
        summary: summaryText,
        description: runOverviewDescription,
        labels: parseLabelsInput(runOverviewLabelsInput),
        attachments: runOverviewAttachments,
      });

      const ticketKey = ticket?.key || 'your request';
      if (ticket?.key) {
        registerTicket(ticket.key, 'support', summaryText);
        setActiveTab('tickets');
        await loadTicketInsights(ticket.key);
      }
      setRunOverviewSuccess(`Thanks! We've created Jira ticket ${ticketKey} with your run overview.`);
    } catch (error: any) {
      const apiError =
        error?.response?.data?.data?.messages?.join(', ') ||
        error?.message ||
        'Something went wrong while creating the support ticket.';
      setRunOverviewError(apiError);
    } finally {
      setRunOverviewSubmitting(false);
    }
  };

  const handleAttachmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    if (files.length === 0) {
      return;
    }

    if (featureSuccess) {
      setFeatureSuccess(null);
    }

    const { next, added, processed } = mergeAttachments(attachments, files);

    if (processed === 0) {
      event.target.value = '';
      return;
    }

    if (added > 0) {
      setAttachments(next);
    }

    event.target.value = '';
  };

  const handleSupportAttachmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    if (files.length === 0) {
      return;
    }

    if (supportSuccess) {
      setSupportSuccess(null);
    }

    const { next, added, processed } = mergeAttachments(supportAttachments, files);

    if (processed === 0) {
      event.target.value = '';
      return;
    }

    if (added > 0) {
      setSupportAttachments(next);
    }

    event.target.value = '';
  };

  const handleRemoveAttachment = (index: number) => {
    if (featureSuccess) {
      setFeatureSuccess(null);
    }
    setAttachments((previous) => previous.filter((_, attachmentIndex) => attachmentIndex !== index));
  };

  const handleRemoveSupportAttachment = (index: number) => {
    if (supportSuccess) {
      setSupportSuccess(null);
    }
    setSupportAttachments((previous) => previous.filter((_, attachmentIndex) => attachmentIndex !== index));
  };

  const handleSubmitFeatureRequest = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setFeatureSuccess(null);

    if (!featureSummary.trim() || !hasRichTextContent(featureDescription)) {
      setFeatureError('Please add a short summary and description for your feature request.');
      return;
    }

    const projectKey = selectedProject || projects[0]?.key;
    const summary = featureSummary.trim();

    if (!projectKey) {
      setFeatureError('A Jira project is required to create a feature request.');
      return;
    }

    setSubmittingTicket(true);
    setFeatureError(null);

    try {
      const ticket = await createFeatureRequestTicket({
        projectKey,
        summary,
        description: featureDescription,
        labels: parseLabelsInput(featureLabelsInput),
        attachments,
      });

      const ticketKey = ticket?.key || 'your request';
      if (ticket?.key) {
        registerTicket(ticket.key, 'feature', summary);
        setActiveTab('tickets');
        await loadTicketInsights(ticket.key);
      }
      resetFeatureFormState();
      setFeatureSuccess(`Thanks! We've created Jira ticket ${ticketKey}. Our product team will review it shortly.`);
    } catch (error: any) {
      const apiError =
        error?.response?.data?.data?.messages?.join(', ') ||
        error?.message ||
        'Something went wrong while creating the feature request.';
      setFeatureError(apiError);
    } finally {
      setSubmittingTicket(false);
    }
  };

  const handleSubmitSupportTicket = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setSupportSuccess(null);

    if (!supportSummary.trim() || !hasRichTextContent(supportDescription)) {
      setSupportError('Please provide a short summary and details for your support ticket.');
      return;
    }

    const projectKey = selectedProject || projects[0]?.key;
    const summary = supportSummary.trim();

    if (!projectKey) {
      setSupportError('A Jira project is required to contact support.');
      return;
    }

    setSupportSubmitting(true);
    setSupportError(null);

    try {
      const ticket = await createSupportTicket({
        projectKey,
        summary,
        description: supportDescription,
        attachments: supportAttachments,
      });

      const ticketKey = ticket?.key || 'your ticket';
      if (ticket?.key) {
        registerTicket(ticket.key, 'support', summary);
        setActiveTab('tickets');
        await loadTicketInsights(ticket.key);
      }
      resetSupportFormState();
      setExpandedTextarea((previous) => (previous === 'support-description' ? null : previous));
      setSupportSuccess(`Thanks! We've created Jira ticket ${ticketKey}. Our support team will reach out soon.`);
    } catch (error: any) {
      const apiError =
        error?.response?.data?.data?.messages?.join(', ') ||
        error?.message ||
        'Something went wrong while creating the support ticket.';
      setSupportError(apiError);
    } finally {
      setSupportSubmitting(false);
    }
  };

  const helpfulResources = useMemo(
    () => [
      {
        title: 'Product documentation',
        description: 'Learn how Lens works and discover product tips.',
        href: 'https://docs.lensapp.com',
      },
      {
        title: 'Status page',
        description: 'Check the real-time health of Lens services.',
        href: 'https://status.lensapp.com',
      },
      {
        title: 'Community forum',
        description: 'Connect with other Lens users and share insights.',
        href: 'https://community.lensapp.com',
      },
    ],
    []
  );

  const renderBody = () => {
    switch (activeTab) {
      case 'feature':
        return (
          <div className="support-chatbot__pane support-chatbot__pane--feature">
            <div className="support-chatbot__pane-intro">
              <h4>Share your idea ✨</h4>
              <p>Tell us what would make Lens better for you. Your request goes straight to our product team.</p>
            </div>
            <form className="support-chatbot__form" onSubmit={handleSubmitFeatureRequest}>
              <label htmlFor="support-chatbot-project">Jira project</label>
              {projectsLoading ? (
                <div className="support-chatbot__loading">
                  <CircularProgress size={16} />
                  <span>Loading projects…</span>
                </div>
              ) : (
                <select
                  id="support-chatbot-project"
                  value={selectedProject}
                  onChange={(event) => setSelectedProject(event.target.value)}
                >
                  {projects.map((project) => (
                    <option value={project.key} key={project.key}>
                      {project.name}
                    </option>
                  ))}
                </select>
              )}

              <label htmlFor="support-chatbot-summary">Feature title</label>
              <input
                id="support-chatbot-summary"
                value={featureSummary}
                onChange={(event) => {
                  setFeatureSummary(event.target.value);
                  if (featureSuccess) {
                    setFeatureSuccess(null);
                  }
                  if (featureError) {
                    setFeatureError(null);
                  }
                }}
                placeholder="Add a short, descriptive title"
              />

              <label htmlFor="support-chatbot-description">What would you like us to build?</label>
              <div
                className={`support-chatbot__textarea-wrapper ${
                  expandedTextarea === 'feature-description' ? 'support-chatbot__textarea-wrapper--expanded' : ''
                }`}
              >
                <Richtexteditor
                  id="support-chatbot-description"
                  className="support-chatbot__richtext-editor"
                  value={featureDescription}
                  onChange={(content) => {
                    const nextValue = sanitizeRichTextValue(content);
                    setFeatureDescription(nextValue);
                    if (featureSuccess) {
                      setFeatureSuccess(null);
                    }
                    if (featureError) {
                      setFeatureError(null);
                    }
                  }}
                  placeholder="Share details that help us understand the value of this feature"
                />
                <button
                  type="button"
                  className="support-chatbot__textarea-expand"
                  onClick={() => toggleTextareaExpansion('feature-description')}
                  aria-label={
                    expandedTextarea === 'feature-description'
                      ? 'Collapse feature description field'
                      : 'Expand feature description field'
                  }
                >
                  {expandedTextarea === 'feature-description' ? 'Collapse' : 'Expand'}
                </button>
              </div>

              <label htmlFor="support-chatbot-attachments">Add images or videos (optional)</label>
              <input
                id="support-chatbot-attachments"
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={handleAttachmentChange}
              />

              {attachments.length > 0 && (
                <div className="support-chatbot__attachments">
                  <span className="support-chatbot__attachments-title">Attachments</span>
                  <ul className="support-chatbot__attachment-list">
                    {attachments.map((file, index) => (
                      <li key={`${file.name}-${file.lastModified}-${index}`} className="support-chatbot__attachment">
                        <span className="support-chatbot__attachment-name">{file.name}</span>
                        <button
                          type="button"
                          className="support-chatbot__attachment-remove"
                          onClick={() => handleRemoveAttachment(index)}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {featureSuccess && (
                <div className="support-chatbot__status">{featureSuccess}</div>
              )}

              {featureError && (
                <div className="support-chatbot__status support-chatbot__status--error">{featureError}</div>
              )}

              <div className="support-chatbot__form-actions">
                <button type="button" onClick={handleCloseFeatureForm} disabled={submittingTicket}>
                  Cancel
                </button>
                <button type="submit" disabled={submittingTicket}>
                  {submittingTicket ? (
                    <>
                      <CircularProgress size={16} sx={{ color: '#fff' }} />
                      &nbsp;Sending
                    </>
                  ) : (
                    'Submit request'
                  )}
                </button>
              </div>
            </form>
          </div>
        );
      case 'support':
        return (
          <div className="support-chatbot__pane support-chatbot__pane--support">
            <div className="support-chatbot__pane-intro">
              <h4>Contact support 🙋‍♀️</h4>
              <p>Describe what you need help with and we'll open a ticket with our support team.</p>
            </div>

            <form className="support-chatbot__form" onSubmit={handleSubmitSupportTicket}>
              <label htmlFor="support-chatbot-support-project">Jira project</label>
              {projectsLoading ? (
                <div className="support-chatbot__loading">
                  <CircularProgress size={16} />
                  <span>Loading projects…</span>
                </div>
              ) : (
                <select
                  id="support-chatbot-support-project"
                  value={selectedProject}
                  onChange={(event) => setSelectedProject(event.target.value)}
                >
                  {projects.map((project) => (
                    <option value={project.key} key={project.key}>
                      {project.name}
                    </option>
                  ))}
                </select>
              )}

              <label htmlFor="support-chatbot-support-summary">Issue summary</label>
              <input
                id="support-chatbot-support-summary"
                value={supportSummary}
                onChange={(event) => {
                  setSupportSummary(event.target.value);
                  if (supportSuccess) {
                    setSupportSuccess(null);
                  }
                  if (supportError) {
                    setSupportError(null);
                  }
                }}
                placeholder="Add a short, descriptive summary"
              />

              <label htmlFor="support-chatbot-support-description">How can we help?</label>
              <div
                className={`support-chatbot__textarea-wrapper ${
                  expandedTextarea === 'support-description'
                    ? 'support-chatbot__textarea-wrapper--expanded'
                    : ''
                }`}
              >
                <Richtexteditor
                  id="support-chatbot-support-description"
                  className="support-chatbot__richtext-editor"
                  value={supportDescription}
                  onChange={(content) => {
                    const nextValue = sanitizeRichTextValue(content);
                    setSupportDescription(nextValue);
                    if (supportSuccess) {
                      setSupportSuccess(null);
                    }
                    if (supportError) {
                      setSupportError(null);
                    }
                  }}
                  placeholder="Share any steps to reproduce, expected outcomes, or screenshots"
                />
                <button
                  type="button"
                  className="support-chatbot__textarea-expand"
                  onClick={() => toggleTextareaExpansion('support-description')}
                  aria-label={
                    expandedTextarea === 'support-description'
                      ? 'Collapse support description field'
                      : 'Expand support description field'
                  }
                >
                  {expandedTextarea === 'support-description' ? 'Collapse' : 'Expand'}
                </button>
              </div>

              <label htmlFor="support-chatbot-support-attachments">Add images or videos (optional)</label>
              <input
                id="support-chatbot-support-attachments"
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={handleSupportAttachmentChange}
              />

              {supportAttachments.length > 0 && (
                <div className="support-chatbot__attachments">
                  <span className="support-chatbot__attachments-title">Attachments</span>
                  <ul className="support-chatbot__attachment-list">
                    {supportAttachments.map((file, index) => (
                      <li key={`${file.name}-${file.lastModified}-${index}`} className="support-chatbot__attachment">
                        <span className="support-chatbot__attachment-name">{file.name}</span>
                        <button
                          type="button"
                          className="support-chatbot__attachment-remove"
                          onClick={() => handleRemoveSupportAttachment(index)}
                          disabled={supportSubmitting}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {supportSuccess && <div className="support-chatbot__status">{supportSuccess}</div>}

              {supportError && (
                <div className="support-chatbot__status support-chatbot__status--error">{supportError}</div>
              )}

              <div className="support-chatbot__form-actions">
                <button type="button" onClick={handleCloseFeatureForm} disabled={supportSubmitting}>
                  Cancel
                </button>
                <button type="submit" disabled={supportSubmitting}>
                  {supportSubmitting ? (
                    <>
                      <CircularProgress size={16} sx={{ color: '#fff' }} />
                      &nbsp;Contact support
                    </>
                  ) : (
                    'Contact support'
                  )}
                </button>
              </div>
            </form>
          </div>
        );
      case 'run':
        return (
          <div className="support-chatbot__pane support-chatbot__pane--run">
            <div className="support-chatbot__pane-intro">
              <h4>Run overview 🧪</h4>
              <p>Review the captured run summary before sending it to support.</p>
            </div>

            <div className="support-chatbot__run-overview">
              <label htmlFor="support-chatbot-run-summary">Summary</label>
              <div
                className={`support-chatbot__textarea-wrapper ${
                  expandedTextarea === 'run-summary' ? 'support-chatbot__textarea-wrapper--expanded' : ''
                }`}
              >
                <Richtexteditor
                  id="support-chatbot-run-summary"
                  className="support-chatbot__richtext-editor"
                  value={runOverviewSummary}
                  onChange={(content) => {
                    const nextValue = sanitizeRichTextValue(content);
                    setRunOverviewSummary(nextValue);
                    if (runOverviewError) {
                      setRunOverviewError(null);
                    }
                    if (runOverviewSuccess) {
                      setRunOverviewSuccess(null);
                    }
                  }}
                  placeholder="Add a concise summary of the run"
                />
                <button
                  type="button"
                  className="support-chatbot__textarea-expand"
                  onClick={() => toggleTextareaExpansion('run-summary')}
                  aria-label={
                    expandedTextarea === 'run-summary'
                      ? 'Collapse run summary field'
                      : 'Expand run summary field'
                  }
                >
                  {expandedTextarea === 'run-summary' ? 'Collapse' : 'Expand'}
                </button>
              </div>

              <label htmlFor="support-chatbot-run-details">Run details</label>
              <div
                className={`support-chatbot__textarea-wrapper ${
                  expandedTextarea === 'run-details' ? 'support-chatbot__textarea-wrapper--expanded' : ''
                }`}
              >
                <Richtexteditor
                  id="support-chatbot-run-details"
                  className="support-chatbot__richtext-editor support-chatbot__richtext-editor--run-details"
                  value={runOverviewDescription}
                  onChange={(content) => {
                    const nextValue = sanitizeRichTextValue(content);
                    setRunOverviewDescription(nextValue);
                    if (runOverviewError) {
                      setRunOverviewError(null);
                    }
                    if (runOverviewSuccess) {
                      setRunOverviewSuccess(null);
                    }
                  }}
                  placeholder="Paste run context to share with support"
                />
                <button
                  type="button"
                  className="support-chatbot__textarea-expand"
                  onClick={() => toggleTextareaExpansion('run-details')}
                  aria-label={
                    expandedTextarea === 'run-details'
                      ? 'Collapse run details field'
                      : 'Expand run details field'
                  }
                >
                  {expandedTextarea === 'run-details' ? 'Collapse' : 'Expand'}
                </button>
              </div>
            </div>

            {runOverviewAttachments.length > 0 && (
              <div className="support-chatbot__attachments">
                <span className="support-chatbot__attachments-title">Included attachments</span>
                <ul className="support-chatbot__attachment-list">
                  {runOverviewAttachments.map((file, index) => (
                    <li key={`${file.name}-${file.lastModified}-${index}`} className="support-chatbot__attachment">
                      <span className="support-chatbot__attachment-name">{file.name}</span>
                      <button
                        type="button"
                        className="support-chatbot__attachment-remove"
                        onClick={() => handleRemoveRunOverviewAttachment(index)}
                        disabled={runOverviewSubmitting}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {runOverviewSuccess && <div className="support-chatbot__status">{runOverviewSuccess}</div>}

            {runOverviewError && (
              <div className="support-chatbot__status support-chatbot__status--error">{runOverviewError}</div>
            )}

            <div className="support-chatbot__form-actions">
              <button
                type="button"
                onClick={() => {
                  setRunOverviewError(null);
                  setRunOverviewSuccess(null);
                  setActiveTab('feature');
                }}
                disabled={runOverviewSubmitting}
              >
                Feature requests
              </button>
              <button type="button" onClick={handleCreateRunOverviewTicket} disabled={runOverviewSubmitting}>
                {runOverviewSubmitting ? (
                  <>
                    <CircularProgress size={16} sx={{ color: '#fff' }} />
                    &nbsp;Contact support
                  </>
                ) : (
                  'Contact support'
                )}
              </button>
            </div>
          </div>
        );
      case 'tickets': {
        const descriptionText = ticketDetails?.description
          ? normalizeCommentBody(ticketDetails.description)
          : '';
        const hasTicketSelected = Boolean(selectedTicketKey);
        const ticketProject =
          typeof ticketDetails?.project === 'string'
            ? ticketDetails.project
            : ticketDetails?.project?.name || ticketDetails?.project?.key || '';
        const ticketType =
          ticketDetails?.issueType?.name || ticketDetails?.issueType || ticketDetails?.type || '';
        const ticketAssignee =
          ticketDetails?.assignee?.displayName ||
          ticketDetails?.assignee?.name ||
          ticketDetails?.assignee?.emailAddress ||
          ticketDetails?.assignee ||
          '';
        const ticketLabels = extractTicketLabels(ticketDetails?.labels);

        return (
          <div className="support-chatbot__pane support-chatbot__pane--tickets">
            <div className="support-chatbot__pane-intro">
              <h4>Track your Jira tickets 🎫</h4>
              <p>Monitor feature ideas and support requests without leaving Lens.</p>
            </div>

            <form className="support-chatbot__ticket-lookup" onSubmit={handleTicketLookupSubmit}>
              <label htmlFor="support-chatbot-ticket-lookup">Ticket key</label>
              <div className="support-chatbot__ticket-lookup-row">
                <input
                  id="support-chatbot-ticket-lookup"
                  value={ticketLookup}
                  onChange={(event) => {
                    setTicketLookup(event.target.value.toUpperCase());
                    setTicketDetailsError(null);
                  }}
                  placeholder="e.g. LENS-2034"
                  autoComplete="off"
                />
                <button type="submit" disabled={ticketDetailsLoading}>
                  {ticketDetailsLoading ? 'Loading…' : 'Check status'}
                </button>
              </div>
            </form>

            {trackedTickets.length > 0 && (
              <div className="support-chatbot__ticket-recent">
                <span className="support-chatbot__ticket-recent-title">Recent tickets</span>
                <div className="support-chatbot__ticket-chip-group">
                  {trackedTickets.map((ticket) => (
                    <button
                      type="button"
                      key={ticket.key}
                      className="support-chatbot__ticket-chip"
                      onClick={() => handleSelectTrackedTicket(ticket)}
                      title={ticket.summary}
                    >
                      <span className="support-chatbot__ticket-chip-key">{ticket.key}</span>
                      <span
                        className={`support-chatbot__ticket-chip-type support-chatbot__ticket-chip-type--${ticket.type}`}
                      >
                        {ticket.type === 'support' ? 'Support' : 'Feature'}
                      </span>
                      <span
                        role="button"
                        tabIndex={0}
                        className="support-chatbot__ticket-chip-remove"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          handleRemoveTrackedTicket(ticket.key);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            event.stopPropagation();
                            handleRemoveTrackedTicket(ticket.key);
                          }
                        }}
                        aria-label={`Remove ${ticket.key} from quick access`}
                      >
                        ×
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {ticketDetailsError && (
              <div className="support-chatbot__status support-chatbot__status--error">{ticketDetailsError}</div>
            )}

            {ticketDetailsLoading && (
              <div className="support-chatbot__ticket-loading">
                <CircularProgress size={18} />
                <span>Fetching ticket details…</span>
              </div>
            )}

            {!ticketDetailsLoading && !ticketDetails && !ticketDetailsError && (
              <div className="support-chatbot__ticket-placeholder">
                <p>Select a ticket above to view its live status, history, and conversation.</p>
              </div>
            )}

            {ticketDetails && !ticketDetailsLoading && (
              <div className="support-chatbot__ticket-details-card">
                <div className="support-chatbot__ticket-details-header">
                  <div>
                    <h5>{ticketDetails.summary || 'Ticket details'}</h5>
                    <span className="support-chatbot__ticket-key">{selectedTicketKey}</span>
                  </div>
                  {ticketDetails.status && (
                    <span
                      className={`support-chatbot__ticket-status support-chatbot__ticket-status--${String(
                        ticketDetails.status
                      )
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, '-')}`}
                    >
                      {ticketDetails.status}
                    </span>
                  )}
                </div>

                <dl className="support-chatbot__ticket-metadata">
                  {ticketProject && (
                    <div>
                      <dt>Project</dt>
                      <dd>{ticketProject}</dd>
                    </div>
                  )}
                  {ticketType && (
                    <div>
                      <dt>Type</dt>
                      <dd>{ticketType}</dd>
                    </div>
                  )}
                  {ticketAssignee && (
                    <div>
                      <dt>Assignee</dt>
                      <dd>{ticketAssignee}</dd>
                    </div>
                  )}
                  {ticketLabels.length > 0 && (
                    <div>
                      <dt>Labels</dt>
                      <dd>{ticketLabels.join(', ')}</dd>
                    </div>
                  )}
                  {(ticketDetails.updated || ticketDetails.created) && (
                    <div>
                      <dt>Updated</dt>
                      <dd>{formatDateTime(ticketDetails.updated || ticketDetails.created)}</dd>
                    </div>
                  )}
                </dl>

                {descriptionText && (
                  <p className="support-chatbot__ticket-description">{descriptionText}</p>
                )}
              </div>
            )}

            <div className="support-chatbot__ticket-comments">
              <div className="support-chatbot__ticket-comments-heading">
                <h6>Comments</h6>
                {ticketCommentsLoading && <CircularProgress size={16} />}
              </div>

              {commentsError && (
                <div className="support-chatbot__status support-chatbot__status--error">{commentsError}</div>
              )}

              {hasTicketSelected && !ticketCommentsLoading && ticketComments.length === 0 && !commentsError && (
                <p className="support-chatbot__ticket-comment-empty">
                  No comments yet. Share the latest updates with your teammates.
                </p>
              )}

              {ticketCommentsLoading && (
                <div className="support-chatbot__ticket-loading support-chatbot__ticket-loading--inline">
                  <CircularProgress size={16} />
                  <span>Loading comments…</span>
                </div>
              )}

              {hasTicketSelected && ticketComments.length > 0 && (
                <ul className="support-chatbot__ticket-comment-list">
                  {ticketComments.map((comment) => (
                    <li key={comment.id} className="support-chatbot__ticket-comment-item">
                      <div className="support-chatbot__ticket-comment-meta">
                        <span>{comment.author}</span>
                        <span>{formatDateTime(comment.updated || comment.created)}</span>
                      </div>
                      {editingCommentId === comment.id ? (
                        <>
                          <textarea
                            className="support-chatbot__ticket-comment-input"
                            value={editingCommentDraft}
                            onChange={(event) => setEditingCommentDraft(event.target.value)}
                            rows={3}
                            disabled={commentSubmitting}
                          />
                          <div className="support-chatbot__ticket-comment-actions">
                            <button type="button" onClick={handleCancelEditComment} disabled={commentSubmitting}>
                              Cancel
                            </button>
                            <button type="button" onClick={handleUpdateComment} disabled={commentSubmitting}>
                              {commentSubmitting ? 'Saving…' : 'Update'}
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <p>{comment.body || '—'}</p>
                          <button
                            type="button"
                            className="support-chatbot__ticket-comment-edit"
                            onClick={() => handleStartEditComment(comment)}
                          >
                            Edit
                          </button>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              <div className="support-chatbot__ticket-comment-form">
                <label htmlFor="support-chatbot-ticket-comment">Add a comment</label>
                <textarea
                  id="support-chatbot-ticket-comment"
                  className="support-chatbot__ticket-comment-input"
                  value={commentDraft}
                  onChange={(event) => setCommentDraft(event.target.value)}
                  placeholder={hasTicketSelected ? 'Share an update for your team…' : 'Lookup a ticket to enable comments.'}
                  rows={3}
                  disabled={commentSubmitting || !hasTicketSelected}
                />
                <button
                  type="button"
                  onClick={handleAddComment}
                  disabled={commentSubmitting || !commentDraft.trim() || !hasTicketSelected}
                >
                  {commentSubmitting ? 'Posting…' : 'Post comment'}
                </button>
              </div>
            </div>
          </div>
        );
      }
      case 'resources':
        return (
          <div className="support-chatbot__pane support-chatbot__pane--resources">
            <div className="support-chatbot__pane-intro">
              <h4>Helpful resources 📚</h4>
              <p>Browse guides and community spaces while we work on your request.</p>
            </div>
            <ul className="support-chatbot__resource-list">
              {helpfulResources.map((resource) => (
                <li key={resource.href} className="support-chatbot__resource">
                  <div>
                    <h5>{resource.title}</h5>
                    <p>{resource.description}</p>
                  </div>
                  <a href={resource.href} target="_blank" rel="noreferrer">
                    Open
                    <ArrowForwardIcon fontSize="inherit" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        );
      default:
        return null;
    }
  };

  if (hidden) {
    return null;
  }

  const rootClassName = `support-chatbot ${hidden ? 'support-chatbot--hidden' : ''} ${
    isExpanded ? 'support-chatbot--expanded' : ''
  }`;

  return (
    <div className={rootClassName}>
      {isOpen && (
        <div
          className={`support-chatbot__panel ${isExpanded ? 'support-chatbot__panel--expanded' : ''}`}
          id="support-chatbot"
        >
          <div className="support-chatbot__header">
            <div>
              <h3>Ask Ash</h3>
              <span>Powered by Lens Assist</span>
            </div>
            <div className="support-chatbot__header-actions">
              <button
                type="button"
                className="support-chatbot__resize"
                onClick={togglePanelExpansion}
                aria-label={isExpanded ? 'Exit expanded view' : 'Expand support chat'}
              >
                {isExpanded ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
              </button>
              <button
                type="button"
                className="support-chatbot__close"
                onClick={handleToggle}
                aria-label="Close support chat"
              >
                <CloseIcon fontSize="small" />
              </button>
            </div>
          </div>

          <div className="support-chatbot__tabs" role="tablist">
            <button
              type="button"
              className={`support-chatbot__tab ${activeTab === 'feature' ? 'support-chatbot__tab--active' : ''}`}
              onClick={() => setActiveTab('feature')}
              role="tab"
              aria-selected={activeTab === 'feature'}
            >
              <AutoAwesomeIcon fontSize="small" />
              <span>Feature request</span>
            </button>
            <button
              type="button"
              className={`support-chatbot__tab ${activeTab === 'support' ? 'support-chatbot__tab--active' : ''}`}
              onClick={() => {
                setSupportError(null);
                setSupportSuccess(null);
                setActiveTab('support');
              }}
              role="tab"
              aria-selected={activeTab === 'support'}
            >
              <SupportAgentIcon fontSize="small" />
              <span>Support ticket</span>
            </button>
            <button
              type="button"
              className={`support-chatbot__tab ${activeTab === 'run' ? 'support-chatbot__tab--active' : ''}`}
              onClick={() => {
                setRunOverviewError(null);
                setRunOverviewSuccess(null);
                setActiveTab('run');
              }}
              role="tab"
              aria-selected={activeTab === 'run'}
            >
              <AssessmentIcon fontSize="small" />
              <span>Run overview</span>
            </button>
            <button
              type="button"
              className={`support-chatbot__tab ${activeTab === 'tickets' ? 'support-chatbot__tab--active' : ''}`}
              onClick={() => {
                setTicketDetailsError(null);
                setCommentsError(null);
                setActiveTab('tickets');
              }}
              role="tab"
              aria-selected={activeTab === 'tickets'}
            >
              <ReceiptLongIcon fontSize="small" />
              <span>Ticket status</span>
            </button>
            <button
              type="button"
              className={`support-chatbot__tab ${activeTab === 'resources' ? 'support-chatbot__tab--active' : ''}`}
              onClick={() => setActiveTab('resources')}
              role="tab"
              aria-selected={activeTab === 'resources'}
            >
              <MenuBookIcon fontSize="small" />
              <span>Resources</span>
            </button>
          </div>

          <div className={`support-chatbot__body support-chatbot__body--${activeTab}`}>{renderBody()}</div>
        </div>
      )}

      <button
        type="button"
        className="support-chatbot__toggle"
        onClick={handleToggle}
        aria-expanded={isOpen}
        aria-controls="support-chatbot"
      >
        <span className="support-chatbot__toggle-icon" aria-hidden="true">
          <SupportChatBotLottieIcon isOpen={isOpen} />
        </span>
        {/* {isOpen ? 'Close chat' : 'Support'} */}
      </button>
    </div>
  );
};

export default SupportChatBot;